import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { isBlank, isValidEmail } from '@/lib/validators';

interface InviteEmployeeDialogProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  /** Отправка приглашения; ошибку показывает вызывающий экран. */
  readonly onInvite: (email: string, displayName?: string) => Promise<void>;
}

/** Диалог приглашения сотрудника: спрашиваем только email, имя — по желанию (§8 «минимум полей»). */
export default function InviteEmployeeDialog({ opened, onClose, onInvite }: InviteEmployeeDialogProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    initialValues: { email: '', displayName: '' },
    validate: {
      email: (value: string) => {
        if (isBlank(value)) {
          return t('auth.validation.required');
        }
        return isValidEmail(value) ? null : t('auth.validation.email');
      },
    },
  });

  // Сброс при открытии: диалог переиспользуется, прошлый ввод показывать не нужно.
  // Зависимость только от opened: объект формы пересоздаётся при каждом рендере, а его сброс сам
  // вызывает рендер — включение form в зависимости даёт бесконечный цикл обновлений.
  useEffect(() => {
    if (opened) {
      form.reset();
    }

  }, [opened]);

  const handleSubmit = async (values: { email: string; displayName: string }) => {
    setSubmitting(true);
    try {
      await onInvite(values.email.trim(), values.displayName.trim() || undefined);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('app.company.employees.invite.title')} centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t('auth.fields.email')}
            type="email"
            autoComplete="off"
            required
            key={form.key('email')}
            {...form.getInputProps('email')}
          />
          <TextInput
            label={t('app.company.employees.invite.nameLabel')}
            description={t('app.company.employees.invite.nameHint')}
            key={form.key('displayName')}
            {...form.getInputProps('displayName')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {t('app.company.employees.invite.submit')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
